import { Fragment, useId, useRef } from 'react'
import { manifesto } from '@/content/site'
import { useReducedMotion } from '@/hooks/useReducedMotion'
import { gsap, useGSAP } from '@/lib/gsap'
import { BRUSH_VIEWBOX, brushPath } from './brush'
import styles from './Manifesto.module.css'
import { tokenizeBody, type BodyRun, type BodyWord } from './tokenize'

const RUNS = tokenizeBody(
  manifesto.body,
  manifesto.highlights,
  new Map([[manifesto.karta.latin, 'hi-Latn']]),
)

/** Unlit ink, as a fraction of full opacity. */
const DIM = 0.15
/** How long one word takes to light, in "lines" of reading (the wave's softness). */
const FADE = 0.6

function Word({ word }: { word: BodyWord }) {
  return (
    <span className={styles.word} data-word lang={word.lang}>
      {word.text}
    </span>
  )
}

function Words({ words, first }: { words: BodyWord[]; first: boolean }) {
  return words.map((word, index) => (
    <Fragment key={index}>
      {word.space && !(first && index === 0) ? ' ' : null}
      <Word word={word} />
    </Fragment>
  ))
}

function Highlight({ run, first }: { run: BodyRun; first: boolean }) {
  const gradientId = useId()
  const [lead, ...rest] = run.words
  return (
    <>
      {lead.space && !first ? ' ' : null}
      <em className={styles.highlight} data-highlight>
        <Word word={lead} />
        {rest.map((word, index) => (
          <Fragment key={index}>
            {word.space ? ' ' : null}
            <Word word={word} />
          </Fragment>
        ))}
        <svg
          className={styles.brush}
          viewBox={BRUSH_VIEWBOX}
          preserveAspectRatio="none"
          aria-hidden="true"
          focusable="false"
          data-brush
        >
          <defs>
            <linearGradient id={gradientId} x1="0" x2="1" y1="0" y2="0">
              <stop offset="0" style={{ stopColor: 'var(--vk-iris-500)' }} />
              <stop offset="0.7" style={{ stopColor: 'var(--vk-iris-500)' }} />
              <stop
                offset="1"
                style={{
                  stopColor: 'color-mix(in oklab, var(--vk-iris-500), var(--vk-prism-sky) 55%)',
                }}
              />
            </linearGradient>
          </defs>
          <path
            d={brushPath(run.words.map((word) => word.text).join(' '))}
            fill={`url(#${gradientId})`}
          />
        </svg>
      </em>
    </>
  )
}

/**
 * The manifesto statement, scrubbed word by word as you read: each line lights from left to
 * right as it crosses the reading line, words rising from ~15% to full ink. Highlight
 * phrases (Boska italic) warm into iris and a hand-drawn brush stroke draws beneath them.
 * Timing follows the real line breaks, rebuilt when the width or the fonts change.
 * Reduced motion: everything lit, brushes drawn, one gentle fade.
 */
export function Body() {
  const ref = useRef<HTMLParagraphElement>(null)
  const reducedMotion = useReducedMotion()

  useGSAP(
    (_context, contextSafe) => {
      const root = ref.current
      if (!root || !contextSafe) return
      if (reducedMotion) {
        gsap.from(root, {
          opacity: 0,
          duration: 0.3,
          ease: 'none',
          scrollTrigger: { trigger: root, start: 'top 88%', once: true },
        })
        return
      }

      const words = gsap.utils.toArray<HTMLElement>('[data-word]', root)
      const highlights = gsap.utils.toArray<HTMLElement>('[data-highlight]', root)
      const computed = getComputedStyle(root)
      const ink = computed.getPropertyValue('--color-fg').trim()
      const accent = computed.getPropertyValue('--color-accent-text').trim()
      let timeline: gsap.core.Timeline | null = null
      let builtWidth = -1

      const build = contextSafe(() => {
        builtWidth = root.clientWidth
        timeline?.scrollTrigger?.kill()
        timeline?.kill()

        // Group words into the lines they actually wrap to (layout reads here, never on scroll).
        const rects = words.map((word) => word.getBoundingClientRect())
        const lineOf: number[] = []
        const left: number[] = []
        const right: number[] = []
        let line = -1
        let lineTop = Number.NEGATIVE_INFINITY
        rects.forEach((rect, index) => {
          if (rect.top > lineTop + rect.height * 0.5) {
            line += 1
            lineTop = rect.top
            left[line] = rect.left
            right[line] = rect.right
          } else {
            left[line] = Math.min(left[line], rect.left)
            right[line] = Math.max(right[line], rect.right)
          }
          lineOf[index] = line
        })
        const at = rects.map((rect, index) => {
          const l = lineOf[index]
          return l + ((rect.left - left[l]) / Math.max(1, right[l] - left[l])) * 0.85
        })

        const tl = gsap.timeline({
          defaults: { ease: 'sine.inOut', duration: FADE },
          scrollTrigger: { trigger: root, start: 'top 76%', end: 'bottom 54%', scrub: 1 },
        })
        words.forEach((word, index) => {
          const lit = word.closest('[data-highlight]')
            ? { opacity: 1, color: accent }
            : { opacity: 1 }
          const unlit = word.closest('[data-highlight]')
            ? { opacity: DIM, color: ink }
            : { opacity: DIM }
          tl.fromTo(word, unlit, lit, at[index])
        })
        highlights.forEach((highlight) => {
          const brush = highlight.querySelector('[data-brush]')
          const last = Math.max(
            ...Array.from(
              highlight.querySelectorAll('[data-word]'),
              (word) => at[words.indexOf(word as HTMLElement)],
            ),
          )
          if (brush) {
            tl.fromTo(
              brush,
              { clipPath: 'inset(0% 100% 0% 0%)' },
              { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.2, ease: 'power2.inOut' },
              last + FADE * 0.4,
            )
          }
        })
        // Land on the current scroll position instead of scrubbing up to it.
        if (tl.scrollTrigger) tl.progress(tl.scrollTrigger.progress)
        timeline = tl
      })

      build()
      let frame = 0
      const resizeObserver = new ResizeObserver(() => {
        if (root.clientWidth === builtWidth) return
        window.cancelAnimationFrame(frame)
        frame = window.requestAnimationFrame(build)
      })
      resizeObserver.observe(root)
      let cancelled = false
      void document.fonts.ready.then(() => {
        if (!cancelled) build()
      })

      return () => {
        cancelled = true
        window.cancelAnimationFrame(frame)
        resizeObserver.disconnect()
      }
    },
    { scope: ref, dependencies: [reducedMotion], revertOnUpdate: true },
  )

  return (
    <p ref={ref} className={styles.body}>
      {RUNS.map((run, index) =>
        run.highlight === null ? (
          <Words key={index} words={run.words} first={index === 0} />
        ) : (
          <Highlight key={index} run={run} first={index === 0} />
        ),
      )}
    </p>
  )
}
